import { useState } from 'react'
import { BriefcaseBusiness, Clock, UserPlus, Users } from 'lucide-react'
import { EndpointHint, HelpIcon, LoadFailure, Skeleton } from '../../core/index.js'
import { useWorkforcePlan } from '../hooks.js'
import type { WorkforceFlag, WorkforceGroup, WorkforcePlan, WorkforceUnit } from '../types.js'
import { SHIFT_TYPE_LABELS } from './preferencesText.js'

/**
 * The workforce plan on the Reporting tab (census response plan F6, the SNF
 * guide's section 7): how many people the staffing plan needs on the books.
 * Opens with the tiles - hours a day, FTEs needed with the buffer, FTEs the
 * roster holds, hires - then the roles, then each unit's lines on a two-week
 * rotation, every other weekend, with the full-timers who already work them
 * and the pattern on request. A planning view: nothing here changes a month.
 */

const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const
const FLAG_TEXT: Record<WorkforceFlag, string> = {
  no_staffing_plan: 'No staffing plan is set, so nothing says what to hire for. Set one under Configuration › Scheduling › Census.',
  unit_without_census: 'A unit has no planned census and no recent counts, so it is left out. Give it a planned census under Configuration › Details.',
  posted_late: 'The schedule is released less than two weeks before the month. Staff usually want it 2 to 4 weeks ahead: see Configuration › Scheduling › Timeline.',
}
const BASIS_TEXT: Record<WorkforceUnit['basis'], string> = { planned: 'planned census', recent_average: 'recent average', none: 'no census' }

const fte = (value: number): string => (Number.isInteger(value) ? String(value) : value.toFixed(1))

function Stat({ icon, value, label, sub, tone = 'plain' }: { icon: React.ReactNode; value: string; label: string; sub?: string; tone?: 'warn' | 'good' | 'plain' }) {
  const toneClass = tone === 'warn' ? 'text-coral' : tone === 'good' ? 'text-green' : 'text-ink'
  return (
    <div className="flex items-start gap-3 rounded-lg border border-marine/15 bg-white px-3 py-2.5">
      <span className={`mt-0.5 shrink-0 ${toneClass}`}>{icon}</span>
      <div className="min-w-0">
        <div className={`text-xl font-black leading-tight ${toneClass}`}>{value}</div>
        <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{label}</div>
        {sub && <div className="text-[10px] text-ink-mid">{sub}</div>}
      </div>
    </div>
  )
}

/** The two weeks of a group's lines as a grid: a row per line, a column per day, the full-time coverage under it. */
function PatternGrid({ group }: { group: WorkforceGroup }) {
  const { pattern } = group
  return (
    <div className="mt-1 overflow-x-auto">
      <table className="text-[10px]">
        <thead>
          <tr className="text-ink-mid">
            <th className="pr-2 text-left font-bold">Line</th>
            {Array.from({ length: 14 }, (_, day) => (
              <th key={day} className={`w-5 text-center font-bold ${day % 7 >= 5 ? 'text-marine' : ''} ${day === 7 ? 'border-l border-marine/20' : ''}`}>
                {DAY_LETTERS[day % 7]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pattern.lines.map((line) => (
            <tr key={line.line}>
              <td className="pr-2 font-bold text-ink">{line.line}</td>
              {line.days.map((works, day) => (
                <td key={day} className={`text-center ${day === 7 ? 'border-l border-marine/20' : ''}`}>
                  <span aria-label={works ? 'works' : 'off'} className={`inline-block h-3 w-3 rounded-sm ${works ? 'bg-marine' : 'bg-marine/10'}`} />
                </td>
              ))}
            </tr>
          ))}
          <tr className="border-t border-marine/20 text-ink-mid">
            <td className="pr-2 font-bold">On</td>
            {pattern.coverage.map((count, day) => (
              <td key={day} className={`text-center ${count < group.seatsPerDay ? 'font-bold text-coral' : ''} ${day === 7 ? 'border-l border-marine/20' : ''}`}>
                {count}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      <p className="mt-1 text-[10px] text-ink-mid">Two weeks, Monday first. Group A works the first weekend, group B the second. A weekend day short in coral is what the weekend-only lines cover.</p>
    </div>
  )
}

function GroupRow({ group }: { group: WorkforceGroup }) {
  const [open, setOpen] = useState(false)
  const { pattern } = group
  return (
    <li className="py-1.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-[11px]">
        <span className="font-bold text-ink">
          {SHIFT_TYPE_LABELS[group.shiftType]} {group.role}{' '}
          <span className="font-normal text-ink-mid">
            · {group.seatsPerDay} a day{group.shortSeatsPerDay > 0 && ` + a ${group.shortHours} h short shift`} · {group.shiftHours} h shifts
          </span>
        </span>
        <span className="text-ink-mid">
          {pattern.fullTimeLines} full-time {pattern.fullTimeLines === 1 ? 'line' : 'lines'} ({pattern.groups.A} + {pattern.groups.B}, {pattern.shiftsPerWeek} a week)
          {pattern.weekendOnlyLines > 0 && ` · ${pattern.weekendOnlyLines} weekend-only`}
          {group.shortSeatsPerDay > 0 && ` · ${group.shortSeatsPerDay} part-time short-shift`}
        </span>
      </div>
      <div className="text-[10px] text-ink-mid">
        {group.holders.length > 0 ? `Held by ${group.holders.map((holder) => holder.name).join(', ')}` : 'No full-timer works this unit and shift yet'}
        {group.linesOpen > 0 && <span className="font-bold text-coral"> · {group.linesOpen} open</span>}
        {pattern.lines.length > 0 && (
          <button type="button" className="ml-2 font-bold text-marine" onClick={() => setOpen((current) => !current)}>
            {open ? 'Hide the pattern' : 'Show the pattern'}
          </button>
        )}
      </div>
      {open && <PatternGrid group={group} />}
    </li>
  )
}

function UnitSection({ unit }: { unit: WorkforceUnit }) {
  return (
    <li className="py-2">
      <div className="text-xs font-bold text-ink">
        {unit.unitName}{' '}
        <span className="font-normal text-ink-mid">
          · {unit.census === null ? 'no census to plan on' : `${unit.census} residents (${BASIS_TEXT[unit.basis]}${unit.sample ? `, ${unit.sample} counts` : ''})`}
        </span>
      </div>
      {unit.groups.length > 0 && (
        <ul className="divide-y divide-marine/10">
          {unit.groups.map((group) => (
            <GroupRow key={`${group.shiftType}-${group.role}`} group={group} />
          ))}
        </ul>
      )}
    </li>
  )
}

function RolesTable({ plan }: { plan: WorkforcePlan }) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left text-[10px] font-bold uppercase tracking-wide text-marine">
            <th className="py-1 pr-2">Role</th>
            <th className="px-2 text-right">Hours a day</th>
            <th className="px-2 text-right">FTEs needed</th>
            <th className="px-2 text-right">With {plan.bufferPct}%</th>
            <th className="px-2 text-right">On the roster</th>
            <th className="px-2">Heads (FT · PT · PRN · agency)</th>
            <th className="px-2 text-right">Gap</th>
            <th className="px-2 text-right">Hires</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-marine/10">
          {plan.roles.map((role) => (
            <tr key={role.role}>
              <td className="py-1 pr-2 font-bold text-ink">{role.role}</td>
              <td className="px-2 text-right">
                {role.dailyHours}
                {role.targetDailyHours !== role.dailyHours && <span className="text-ink-mid"> ({role.targetDailyHours} target)</span>}
              </td>
              <td className="px-2 text-right">{fte(role.fteNeeded)}</td>
              <td className="px-2 text-right font-bold text-ink">{fte(role.fteWithBuffer)}</td>
              <td className="px-2 text-right">{fte(role.fteOnRoster)}</td>
              <td className="px-2 text-ink-mid">
                {role.headcount.full_time} · {role.headcount.part_time} · {role.headcount.prn} · {role.headcount.agency}
              </td>
              <td className={`px-2 text-right ${role.fteGap > 0 ? 'font-bold text-coral' : 'text-green'}`}>{role.fteGap > 0 ? `${fte(role.fteGap)} short` : `${fte(-role.fteGap)} over`}</td>
              <td className="px-2 text-right font-bold text-ink">{role.hires}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function WorkforcePlanCard({ facilityId }: { facilityId: string }) {
  const query = useWorkforcePlan(facilityId)
  const plan = query.data
  return (
    <section className="rounded border border-marine/15 bg-white p-5">
      <div className="flex items-center gap-1 text-sm font-black text-ink">
        Workforce
        <HelpIcon topic="schedule.workforce-plan" />
        <EndpointHint className="ml-2">GET /facilities/{'{facilityId}'}/reports/workforce</EndpointHint>
      </div>
      <p className="max-w-2xl text-[11px] leading-relaxed text-ink-mid">How many people the staffing plan needs on the books at each unit’s budgeted census: the hours a day as full-time equivalents, with a buffer for PTO, call-offs and turnover, against the roster - and the lines each shift needs on a two-week rotation where full-timers work every other weekend. A plan to hire and build lines from; the month itself is still bid.</p>
      {query.isPending ? (
        <Skeleton className="mt-3" />
      ) : query.isError || !plan ? (
        <LoadFailure size="xs" className="mt-3" message="The workforce plan could not be loaded." onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : (
        <>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={<Clock className="h-4 w-4" />} value={String(plan.totals.dailyHours)} label="Hours a day" sub={`${plan.totals.targetDailyHours} the target asks`} />
            <Stat icon={<BriefcaseBusiness className="h-4 w-4" />} value={fte(plan.totals.fteWithBuffer)} label="FTEs needed" sub={`${fte(plan.totals.fteNeeded)} + ${plan.bufferPct}% buffer, ${plan.hoursPerFte} h an FTE`} />
            <Stat icon={<Users className="h-4 w-4" />} value={fte(plan.totals.fteOnRoster)} label="FTEs on the roster" sub="PRN and agency counted by head" />
            <Stat icon={<UserPlus className="h-4 w-4" />} value={String(plan.totals.hires)} label="Hires" sub={plan.totals.hires > 0 ? 'full-time, or the same hours part-time' : 'the roster covers the plan'} tone={plan.totals.hires > 0 ? 'warn' : 'good'} />
          </div>
          {plan.flags.map((flag) => (
            <p key={flag} className="mt-2 text-[11px] text-coral">
              {FLAG_TEXT[flag]}
            </p>
          ))}
          {plan.roles.length > 0 && <RolesTable plan={plan} />}
          {plan.units.length > 0 && (
            <ul className="mt-3 divide-y divide-marine/10">
              {plan.units.map((unit) => (
                <UnitSection key={unit.unitId} unit={unit} />
              ))}
            </ul>
          )}
          {plan.unplaced.length > 0 && (
            <p className="mt-2 text-[10px] text-ink-mid">
              Not on a line yet: {plan.unplaced.map((worker) => `${worker.name} (${worker.role}, ${worker.reason === 'no_unit' ? 'no home unit' : 'no released month to read a usual shift from'})`).join(', ')}.
            </p>
          )}
        </>
      )}
    </section>
  )
}
